import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

export class ProductPromotionSlider extends Component {
  requiredRefs = ['scroller'];
  #currentIndex = 0;
  #resizeObserver = new ResizeObserver(() => this.#updateButtons());

  get #items() {
    return Array.from(this.refs.scroller.children);
  }

  connectedCallback() {
    super.connectedCallback();
    this.refs.scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#resizeObserver.observe(this.refs.scroller);
    requestAnimationFrame(() => this.#updateButtons());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.scroller?.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver.disconnect();
  }

  #handleScroll = debounce(() => {
    this.#syncIndexFromScroll();
    this.#updateButtons();
  }, 50);

  #syncIndexFromScroll() {
    const { scroller } = this.refs;
    const items = this.#items;
    if (!items.length) return;

    const scrollerLeft = scroller.getBoundingClientRect().left;
    let closestIndex = 0;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const distance = Math.abs(item.getBoundingClientRect().left - scrollerLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.#currentIndex = closestIndex;
  }

  #isAtStart(scroller) {
    return scroller.scrollLeft <= 1;
  }

  #isAtEnd(scroller) {
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    return maxScrollLeft <= 1 || scroller.scrollLeft >= maxScrollLeft - 1;
  }

  #canScroll(scroller) {
    return scroller.scrollWidth - scroller.clientWidth > 1;
  }

  #updateButtons() {
    const { scroller, actions, prev, next } = this.refs;
    const canScroll = this.#canScroll(scroller);

    if (actions) {
      actions.hidden = !canScroll;
    }

    if (!canScroll || !prev || !next) return;

    prev.disabled = this.#isAtStart(scroller);
    next.disabled = this.#isAtEnd(scroller);
  }

  next() {
    const { scroller } = this.refs;
    if (this.#isAtEnd(scroller)) return;

    this.#syncIndexFromScroll();
    const items = this.#items;
    this.#currentIndex = Math.min(this.#currentIndex + 1, items.length - 1);

    items[this.#currentIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });
  }

  previous() {
    const { scroller } = this.refs;
    if (this.#isAtStart(scroller)) return;

    this.#syncIndexFromScroll();
    const items = this.#items;
    this.#currentIndex = Math.max(this.#currentIndex - 1, 0);

    items[this.#currentIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });
  }
}

if (!customElements.get('product-promotion-slider-component')) {
  customElements.define('product-promotion-slider-component', ProductPromotionSlider);
}
